import { useEffect, useState } from 'react';
import { ImageLightbox } from './ImageLightbox';
import { dataViaApi } from '../api/mode';

/**
 * One uploaded file as a chip: photos load a thumbnail (bytes fetched
 * through Graph with the viewer's token) and open a full-screen preview;
 * PDFs open in a new tab via the browser's viewer. If the download fails,
 * falls back to a plain SharePoint link (direct mode only — in server mode
 * only admins can open SharePoint). Shared by the audit record view
 * and the task page.
 */
export function AttachmentChip({
  kind,
  filename,
  url,
  download,
}: {
  kind: string;
  filename: string;
  /** SharePoint webUrl fallback when the token download fails. */
  url: string;
  download: () => Promise<Blob>;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  /** Bumped by "Try again" to re-run the photo download. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (kind !== 'photo') return;
    let objectUrl: string | null = null;
    let cancelled = false;
    download()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, attempt]);

  if (failed) {
    // Server mode: nobody but admins can open SharePoint (Phase 6.5
    // cutover), so no link — say it didn't load and offer another go.
    if (dataViaApi()) {
      return (
        <span className="muted">
          {kind === 'pdf' ? '📄' : '📷'} {filename} (couldn’t load){' '}
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </button>
        </span>
      );
    }
    // Direct mode: download blocked/failed — the SharePoint page link still works.
    return (
      <a className="entity-link" href={url} target="_blank" rel="noreferrer">
        {kind === 'pdf' ? '📄' : '📷'} {filename}
      </a>
    );
  }

  const openPdf = () => {
    void download()
      .then((blob) => {
        const objectUrl = URL.createObjectURL(
          blob.type ? blob : new Blob([blob], { type: 'application/pdf' }),
        );
        window.open(objectUrl, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      })
      .catch(() => setFailed(true));
  };

  return (
    <>
      <button
        type="button"
        className="attach-chip"
        title={
          kind === 'photo' ? 'Click to preview full screen' : 'Click to open the PDF'
        }
        onClick={() => (kind === 'photo' ? setPreviewOpen(true) : openPdf())}
        disabled={kind === 'photo' && blobUrl === null}
      >
        {kind === 'photo' ? (
          blobUrl ? (
            <img src={blobUrl} alt="Attached photo" className="attach-thumb" />
          ) : (
            <span className="attach-icon" aria-hidden>
              📷
            </span>
          )
        ) : (
          <span className="attach-icon" aria-hidden>
            📄
          </span>
        )}
        <span className="attach-meta muted">
          {kind === 'photo' && blobUrl === null ? 'loading…' : filename}
        </span>
      </button>
      {previewOpen && blobUrl && (
        <ImageLightbox
          src={blobUrl}
          alt="Attached photo (full size)"
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}
